import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { generatePseudo } from '@/utils/pseudoGenerator.js';
import { Captcha } from '@/components/Captcha.jsx';
import { ZoneSelector } from '@/features/quiz/ZoneSelector.jsx';

// Écran de profilage avant la révélation du score.
// isAnonymous → demande aussi l'identité (email/pseudo/captcha). Sinon, zone seule.
// onSubmit: ({ zone, email?, pseudo?, captcha_token? }) => Promise
export const QuizzAuthForm = ({ onSubmit, isLoading, error, isAnonymous = true, zones = [] }) => {
    const { t } = useTranslation();

    const [pseudo, setPseudo] = useState('');
    const [email, setEmail] = useState('');
    const [zone, setZone] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [spinning, setSpinning] = useState(false);

    const skipCaptcha = import.meta.env.DEV;

    useEffect(() => {
        if (isAnonymous) setPseudo(generatePseudo());
    }, [isAnonymous]);

    const handleAutoPseudo = (e) => {
        e.preventDefault();
        setSpinning(true);
        setPseudo(generatePseudo());
        setTimeout(() => setSpinning(false), 300);
    };

    const zoneRequired = zones.length > 0;
    const identityValid = !isAnonymous || (acceptTerms && (skipCaptcha || captchaToken));
    const disabled = isLoading || (zoneRequired && !zone) || !identityValid;

    const submit = (e) => {
        e.preventDefault();
        if (disabled) return;
        onSubmit({
            ...(zone ? { zone } : {}),
            ...(isAnonymous ? {
                email,
                pseudo,
                captcha_token: skipCaptcha ? 'bypass-token' : captchaToken,
            } : {}),
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-brand-dark font-sans overflow-hidden select-none flex flex-col">
            {/* Halo */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-action-green/10 blur-[80px]" />
                <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-yellow-400/5 blur-[80px]" />
            </div>

            <div className="relative z-10 flex-1 overflow-y-auto flex flex-col justify-center px-6 py-10">

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-sm mx-auto"
                >
                    {/* Pitch */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-action-green/10 border border-action-green/20 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl">
                            🔓
                        </div>
                        <h2 className="text-3xl font-black text-white uppercase leading-none tracking-tighter mb-3">
                            {t('quiz.auth.title', 'Débloque ton score')}
                        </h2>
                        <p className="text-sm text-gray-400 font-medium leading-relaxed">
                            {isAnonymous
                                ? t('quiz.auth.subtitle', 'Entre dans le classement et révèle tes points exacts.')
                                : t('quiz.auth.subtitle_zone', 'Une dernière chose pour révéler ton score exact.')}
                        </p>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {isAnonymous && (
                            <div className="space-y-3">
                                {/* Pseudo */}
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={t('form.placeholder_pseudo', 'Ton pseudo')}
                                        value={pseudo}
                                        onChange={(e) => setPseudo(e.target.value)}
                                        required
                                        minLength={3}
                                        disabled={isLoading}
                                        className="w-full px-4 py-3.5 pr-20 bg-white/5 border border-white/10 rounded-xl text-white font-medium placeholder-gray-500 focus:outline-none focus:border-action-green/50 focus:bg-white/[0.07] disabled:opacity-50 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAutoPseudo}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/10 border border-white/10 text-xs font-bold text-gray-300 hover:text-white hover:border-white/30 px-3 py-1.5 rounded-lg active:scale-95 transition-all flex items-center gap-1"
                                    >
                                        <span className={spinning ? 'animate-spin' : ''}>⚡️</span>
                                        Auto
                                    </button>
                                </div>

                                {/* Email */}
                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    placeholder={t('form.placeholder_email', 'Ton email')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white font-medium placeholder-gray-500 focus:outline-none focus:border-action-green/50 focus:bg-white/[0.07] disabled:opacity-50 transition-all"
                                />
                            </div>
                        )}

                        {/* Zone (si l'orga en a configuré) */}
                        {zoneRequired && <ZoneSelector zones={zones} value={zone} onSelect={setZone} />}

                        {/* CGU (anonyme uniquement) */}
                        {isAnonymous && (
                            <label className="flex items-start gap-3 p-1 cursor-pointer group">
                                <div className={`relative flex items-center justify-center w-5 h-5 mt-0.5 rounded border transition-colors shrink-0 ${acceptTerms ? 'bg-action-green border-action-green' : 'bg-white/5 border-white/20 group-hover:border-white/40'}`}>
                                    <input
                                        type="checkbox"
                                        required
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                        disabled={isLoading}
                                        className="absolute opacity-0 cursor-pointer w-full h-full"
                                    />
                                    {acceptTerms && (
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <span className="text-xs text-gray-500 leading-relaxed">
                                    {t('quiz.auth.terms_prefix', "J'accepte les")}{' '}
                                    <Link to="/cgu" target="_blank" rel="noopener noreferrer" className="text-gray-300 font-bold underline hover:text-action-green transition-colors">CGU</Link>
                                    {' '}{t('quiz.auth.terms_and', 'et le')}{' '}
                                    <Link to="/rules" target="_blank" rel="noopener noreferrer" className="text-gray-300 font-bold underline hover:text-action-green transition-colors">{t('quiz.auth.terms_rules', 'Règlement')}</Link>.
                                </span>
                            </label>
                        )}

                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-3 rounded-xl font-bold text-center">
                                {t('quiz.auth.error', 'Une erreur est survenue, réessaie.')}
                            </div>
                        )}

                        {isAnonymous && !skipCaptcha && <Captcha onVerify={setCaptchaToken} />}

                        <button
                            type="submit"
                            disabled={disabled}
                            className={`w-full py-4 font-black text-lg uppercase tracking-widest rounded-xl transition-all ${
                                disabled
                                    ? 'bg-white/10 text-gray-500 cursor-not-allowed'
                                    : 'bg-action-green text-white hover:scale-[1.02] active:scale-95 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)]'
                            }`}
                        >
                            {isLoading
                                ? t('quiz.auth.loading', 'Révélation...')
                                : t('quiz.auth.cta', 'Révéler mon score')}
                        </button>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};
